// TablePayments.jsx
import {Button, message, Modal, Popconfirm, Space, Table} from "antd";
import {DeleteOutlined, EditOutlined, PlusOutlined} from '@ant-design/icons';
import {useCallback, useEffect, useState} from "react";
import {instance_2} from "../../../../../API/API";
import TablePaymentsModal from "./TablePaymentsModal";

const TablePayments = ({id, visible, onClose, name, balance, getIncomePeriodic}) => {
    const [loading, setLoading] = useState(false);
    const [transactions, setTransactions] = useState([]);
    const [modalVisible, setModalVisible] = useState(false);
    const [editingPayment, setEditingPayment] = useState(null);
    const [currentBalance, setCurrentBalance] = useState(parseFloat(balance || 0));

    useEffect(() => {
        setCurrentBalance(parseFloat(balance || 0));
    }, [balance]);

    const normalizeTransactions = (data) => {
        const normalized = [];

        // Нормализуем зачисления (payments)
        if (data.payments && Array.isArray(data.payments)) {
            data.payments.forEach(p => {
                normalized.push({
                    id: `p_${p.id}`,
                    originalId: p.id,
                    date: p.date,
                    date_formatted: new Date(p.date * 1000).toLocaleDateString('ru-RU', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric'
                    }).split('.').join('.'),
                    event_type: 'payment',
                    event_name: 'Зачисление',
                    payment_type: p.type,
                    payment_type_name: p.type === 1 ? 'Наличный' : 'Безналичный',
                    amount: p.cost * (100 / 121),
                    amountWithNds: p.cost,
                    nds: p.nds || 0,
                    old_balance: null,
                    new_balance: null,
                    is_editable: true
                });
            });
        }

        // Нормализуем списания (writeoffs)
        if (data.writeoffs && Array.isArray(data.writeoffs)) {
            data.writeoffs.forEach(w => {
                normalized.push({
                    id: `w_${w.id}`,
                    originalId: w.id,
                    date: new Date(w.ts).getTime() / 1000,
                    date_formatted: new Date(w.ts).toLocaleDateString('ru-RU', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric'
                    }).split('.').join('.'),
                    event_type: 'writeoff',
                    event_name: 'Списание',
                    payment_type: w.type,
                    payment_type_name: w.type === 1 ? 'Наличный' : 'Безналичный',
                    amount: w.tax,
                    amountWithNds: w.tax,
                    nds: 0,
                    old_balance: w.old_balance,
                    new_balance: w.new_balance,
                    is_editable: false
                });
            });
        }

        return normalized.sort((a, b) => b.date - a.date);
    };

    const getTransactions = useCallback(async () => {
        if (!id) return;

        try {
            setLoading(true);
            const response = await instance_2.get(`finance/getPayment.php?object_id=${id}`);
            if (response.data) {
                const normalized = normalizeTransactions(response.data);
                setTransactions(normalized);
            }
        } catch (e) {
            console.error('Error fetching transactions:', e);
            message.error('Ошибка при загрузке данных');
        } finally {
            setLoading(false);
        }
    }, [id]);

    const handleDelete = async (record) => {
        try {
            setLoading(true);
            const response = await instance_2.post('finance/deletePayment.php', {
                id: record.originalId
            });

            if (response.data.success) {
                message.success('Платеж удален');
                // Обновляем баланс из ответа
                if (response.data.new_balance !== undefined) {
                    setCurrentBalance(parseFloat(response.data.new_balance));
                }
                await getTransactions();
                // Обновляем данные в родительском компоненте
                if (getIncomePeriodic) {
                    await getIncomePeriodic();
                }
            } else {
                message.error(response.data.error || 'Ошибка при удалении');
            }
        } catch (error) {
            console.error('Error deleting payment:', error);
            message.error('Ошибка при удалении платежа');
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (record) => {
        setEditingPayment(record);
        setModalVisible(true);
    };

    const handleAdd = () => {
        setEditingPayment(null);
        setModalVisible(true);
    };

    const handleModalSuccess = useCallback(async (newBalanceFromResponse) => {
        if (newBalanceFromResponse !== undefined) {
            setCurrentBalance(parseFloat(newBalanceFromResponse));
        }
        await getTransactions();
        // Обновляем данные в родительском компоненте
        if (getIncomePeriodic) {
            await getIncomePeriodic();
        }
    }, [getTransactions, getIncomePeriodic]);

    useEffect(() => {
        if (visible && id) {
            getTransactions();
        }
    }, [id, visible, getTransactions]);

    const columns = [
        {
            title: "Дата",
            dataIndex: "date_formatted",
            key: "date",
            width: 100,
            align: 'center',
        },
        {
            title: "Событие",
            dataIndex: "event_name",
            key: "event",
            width: 100,
            align: 'center',
            filters: [
                {text: 'Зачисление', value: 'payment'},
                {text: 'Списание', value: 'writeoff'},
            ],
            onFilter: (value, record) => record.event_type === value,
        },
        {
            title: "Тип платежа",
            dataIndex: "payment_type_name",
            key: "payment_type",
            width: 100,
            align: 'center',
            filters: [
                {text: 'Наличный', value: 1},
                {text: 'Безналичный', value: 2},
            ],
            onFilter: (value, record) => record.payment_type === value,
        },
        {
            title: "Сумма",
            dataIndex: "amount",
            key: "amount",
            width: 120,
            align: 'right',
            render: (amount) => `€ ${Number(amount).toFixed(2)}`,
            sorter: (a, b) => a.amount - b.amount,
        },
        {
            title: "НДС",
            dataIndex: "nds",
            key: "nds",
            width: 80,
            align: 'right',
            render: (nds) => nds ? `${Number(nds).toFixed(2)}%` : '—',
        },
        {
            title: "Сумма с НДС",
            key: "amountWithNds",
            dataIndex: 'amountWithNds',
            width: 120,
            align: 'right',
            render: (amountWithNds) => amountWithNds ? `€ ${Number(amountWithNds).toFixed(2)}` : ''
        },
        {
            title: "Баланс до",
            dataIndex: "old_balance",
            key: "old_balance",
            width: 100,
            align: 'right',
            render: (balance) => {
                if (balance === null || balance === undefined) return '—';
                return `€ ${Number(balance).toFixed(2)}`;
            },
        },
        {
            title: "Баланс после",
            dataIndex: "new_balance",
            key: "new_balance",
            width: 100,
            align: 'right',
            render: (balance) => {
                if (balance === null || balance === undefined) return '—';
                return `€ ${Number(balance).toFixed(2)}`;
            },
        },
        {
            title: "Действия",
            key: "actions",
            width: 100,
            align: 'center',
            render: (_, record) => {
                if (record.event_type !== 'payment') return null;

                return (
                    <Space size="small">
                        <Button
                            type="text"
                            size="small"
                            icon={<EditOutlined/>}
                            onClick={() => handleEdit(record)}
                        />
                        <Popconfirm
                            title="Удалить платеж"
                            description="Вы уверены, что хотите удалить этот платеж?"
                            onConfirm={() => handleDelete(record)}
                            okText="Да"
                            cancelText="Нет"
                        >
                            <Button
                                type="text"
                                size="small"
                                danger
                                icon={<DeleteOutlined/>}
                            />
                        </Popconfirm>
                    </Space>
                );
            },
        },
    ];

    return (
        <>
            <Modal
                title={
                    <div>
                        <div>{name} | История операций</div>
                        <div style={{
                            color: currentBalance >= 0 ? '#52c41a' : '#f5222d',
                            fontWeight: 'bold',
                            fontSize: '16px'
                        }}>
                            Текущий баланс: €{currentBalance.toFixed(2)}
                        </div>
                    </div>
                }
                open={visible}
                onCancel={onClose}
                footer={null}
                width={1100}
            >
                <div style={{marginBottom: 16, textAlign: "right"}}>
                    <Button
                        type="link"
                        size={'small'}
                        icon={<PlusOutlined/>}
                        onClick={handleAdd}
                    >
                        Добавить платеж
                    </Button>
                </div>

                <Table
                    loading={loading}
                    size="small"
                    bordered
                    columns={columns}
                    dataSource={transactions}
                    pagination={false}
                    rowClassName={(record) =>
                        record.event_type === 'payment' ? 'row-payment' : 'row-writeoff'
                    }
                    rowKey="id"
                    scroll={{y: 400}}
                />
            </Modal>

            <TablePaymentsModal
                visible={modalVisible}
                onClose={() => setModalVisible(false)}
                onSuccess={handleModalSuccess}
                payment={editingPayment}
                objectId={id}
                objectName={name}
                getIncomePeriodic={getIncomePeriodic}
            />
        </>
    );
};

export default TablePayments;